import type { EntryDescriptor, EntryField } from './ClinicalEventEntry';
import type { ProposedEntry } from './PrenatalEntry';
import type { ClinicalEvent } from './types';
import { activeEvents } from './projection';

const options = (values: string[]): EntryField['options'] => values.map((value) => ({ value, label: value }));
const num = (key: string, label: string): EntryField => ({ key, label, kind: 'number' });
const text = (key: string, label: string): EntryField => ({ key, label });
const pick = (key: string, label: string, values: string[]): EntryField => ({ key, label, options: options(values) });
export const maternalPostpartumGroups: Record<string, EntryDescriptor[]> = {
  'Maternal check': [
    {
      eventType: 'PP.STATUS',
      label: 'Postpartum status',
      fields: [text('status', 'Observed postpartum status'), text('note', 'Status note')],
    },
    {
      eventType: 'VITALS.BP',
      label: 'Blood pressure',
      fields: [num('systolic', 'Systolic (mmHg)'), num('diastolic', 'Diastolic (mmHg)')],
    },
    { eventType: 'VITALS.PULSE', label: 'Pulse', fields: [num('rate', 'Pulse (/min)'), text('rhythm', 'Rhythm')] },
    {
      eventType: 'VITALS.TEMP',
      label: 'Temperature',
      fields: [
        num('value_c', 'Temperature (°C)'),
        pick('method', 'Method', ['Oral', 'Axillary', 'Tympanic', 'Temporal', 'Rectal', 'Other']),
      ],
    },
    {
      eventType: 'PP.FUNDUS',
      label: 'Fundus',
      fields: [
        num('relation_to_umbilicus_fb', 'Fingerbreadths relative to umbilicus (−4 to +2)'),
        pick('tone', 'Tone', ['Firm', 'Boggy', 'Other']),
        pick('deviation', 'Deviation', ['Midline', 'Left', 'Right', 'Other']),
      ],
    },
    {
      eventType: 'PP.BLOOD_LOSS',
      label: 'Blood loss added at this check',
      fields: [
        num('amount_ml', 'Additional blood loss (mL)'),
        pick('method', 'Measurement method', ['Estimated', 'Quantified']),
        text('descriptor', 'Description (may be entered without a number)'),
      ],
    },
  ],
  Perineum: [
    {
      eventType: 'PP.PERINEUM',
      label: 'Perineal assessment and repair',
      fields: [
        pick('status', 'Perineal status', [
          'Intact',
          'First degree',
          'Second degree',
          'Third degree',
          'Fourth degree',
          'Episiotomy',
          'Other',
        ]),
        { key: 'other_lacerations', label: 'Other lacerations (one per line)', kind: 'string-array' },
        { key: 'repair', label: 'Repaired', kind: 'boolean' },
        { key: 'repair_detail', label: 'Repair details', kind: 'textarea' },
        text('suture', 'Suture'),
        text('anesthetic', 'Anesthetic'),
        text('edema', 'Edema'),
        text('hematoma', 'Hematoma'),
      ],
    },
  ],
  'Intake / output': [
    {
      eventType: 'IO.INTAKE',
      label: 'Intake',
      fields: [
        pick('type', 'Intake type', ['Oral', 'IV', 'Other']),
        num('amount_ml', 'Intake (mL)'),
        text('description', 'Intake description'),
      ],
    },
    {
      eventType: 'IO.OUTPUT',
      label: 'Output',
      fields: [
        pick('type', 'Output type', ['Urine', 'Stool', 'Emesis', 'Other']),
        num('amount_ml', 'Output (mL)'),
        text('description', 'Output description'),
      ],
    },
  ],
  Medication: [{ eventType: 'MED.ADMIN', label: 'Maternal medication' }],
  Note: [
    {
      eventType: 'NOTE.PROGRESS',
      label: 'Maternal postpartum note',
      fields: [{ key: 'text', label: 'Observation / postpartum status', kind: 'textarea' }],
    },
  ],
};
export const newbornPostpartumGroups: Record<string, EntryDescriptor[]> = {
  'Newborn check': [
    {
      eventType: 'VITALS.PULSE',
      label: 'Heart rate',
      fields: [num('rate', 'Heart rate (/min)'), text('rhythm', 'Rhythm')],
    },
    {
      eventType: 'VITALS.RR',
      label: 'Respirations',
      fields: [num('rate', 'Respiratory rate (/min)'), text('quality', 'Respiratory quality')],
    },
    {
      eventType: 'VITALS.TEMP',
      label: 'Temperature',
      fields: [num('value_c', 'Temperature (°C)'), pick('method', 'Method', ['Axillary', 'Rectal', 'Other'])],
    },
    {
      eventType: 'VITALS.SPO2',
      label: 'Oxygen saturation',
      fields: [num('value_pct', 'SpO₂ (%)'), text('site', 'Measurement site')],
    },
    { eventType: 'NB.COLOR', label: 'Color', fields: [text('value', 'Observed color')] },
    {
      eventType: 'IO.OUTPUT',
      label: 'Void / stool',
      fields: [
        pick('type', 'Output type', ['Urine', 'Stool', 'Other']),
        num('amount_ml', 'Output (mL, if measured)'),
        text('description', 'Output description'),
      ],
    },
  ],
  'Feeding / location': [
    {
      eventType: 'NB.FEEDING',
      label: 'Feeding',
      fields: [
        pick('method', 'Feeding method', ['Breastfeeding', 'Expressed milk', 'Donor milk', 'Formula', 'Other']),
        { key: 'first_latch', label: 'First latch', kind: 'boolean' },
        pick('side', 'Side', ['Left', 'Right', 'Both']),
        num('duration_min', 'Duration (minutes)'),
        num('latch_score', 'LATCH score (if assessed)'),
      ],
    },
    {
      eventType: 'NB.LOCATION',
      label: 'Location',
      fields: [
        pick('value', 'Newborn location', ['Skin-to-skin', 'With parent', 'Warmer', 'Bassinet', 'Other']),
        { key: 'in_water', label: 'In water', kind: 'boolean' },
      ],
    },
  ],
  Measurements: [
    {
      eventType: 'NB.MEASUREMENTS',
      label: 'Newborn measurements',
      fields: [
        num('weight_g', 'Weight (g)'),
        num('length_cm', 'Length (cm)'),
        num('head_circ_cm', 'Head circumference (cm)'),
        num('chest_circ_cm', 'Chest circumference (cm)'),
      ],
    },
  ],
  Resuscitation: [
    {
      eventType: 'NB.RESUSCITATION',
      label: 'Resuscitation documentation',
      fields: [
        { key: 'interventions', label: 'Interventions performed (one per line)', kind: 'string-array' },
        { key: 'started_at', label: 'Started', kind: 'datetime' },
        { key: 'stopped_at', label: 'Stopped', kind: 'datetime' },
        { key: 'notes', label: 'Response / observations', kind: 'textarea' },
      ],
    },
  ],
  'Blood type / Rh': [
    {
      eventType: 'NB.BLOOD_TYPE',
      label: 'Newborn blood type result',
      fields: [
        pick('rh', 'Rh result', ['Positive', 'Negative', 'Unknown']),
        pick('abo', 'ABO group', ['A', 'B', 'AB', 'O', 'Unknown']),
        text('source', 'Result source'),
      ],
    },
  ],
  Screening: [
    {
      eventType: 'NB.SCREEN',
      label: 'Newborn screening',
      fields: [
        pick('type', 'Screening type', ['CCHD', 'Metabolic', 'Hearing', 'Bilirubin', 'Other']),
        text('result', 'Documented result / interpretation'),
        { key: 'values', label: 'Measured values', kind: 'json' },
      ],
    },
  ],
};
export const newbornMedications = ['Vitamin K', 'Eye prophylaxis', 'Hepatitis B vaccine'] as const;
export function newbornMedicationDescriptor(drug: string): EntryDescriptor {
  return {
    eventType: 'MED.ADMIN',
    label: drug,
    initialPayload: { drug },
    fields: [
      pick('drug', 'Medication', [drug]),
      pick('status', 'Status', ['Administered', 'Declined', 'Deferred']),
      num('dose', 'Dose'),
      text('unit', 'Dose unit'),
      text('route', 'Route'),
      text('site', 'Site'),
      text('lot', 'Lot'),
      text('expiration', 'Expiration (YYYY-MM-DD)'),
    ],
  };
}
/** Retain explicit declines as consent evidence in the same atomic submission. */
export function withNewbornDeclines(entries: ProposedEntry[]): ProposedEntry[] {
  const result = [...entries];
  for (const entry of entries) {
    if (entry.event_type !== 'MED.ADMIN' || !['Declined', 'Deferred'].includes(String(entry.payload.status))) continue;
    const decision = entry.payload.status === 'Declined' ? 'DECLINED' : 'DEFERRED';
    if (
      !result.some(
        (e) =>
          e.event_type === 'CONSENT.DECISION' &&
          e.subject_id === entry.subject_id &&
          e.payload.test === entry.payload.drug &&
          e.payload.decision === decision,
      )
    )
      result.push({
        event_type: 'CONSENT.DECISION',
        subject_id: entry.subject_id,
        payload: { test: entry.payload.drug, decision },
      });
  }
  return result;
}
export function lastMedicationStatus(events: ClinicalEvent[], subjectId: string, drug: string): string {
  return (
    (activeEvents(events)
      .filter((e) => e.subject_id === subjectId && e.event_type === 'MED.ADMIN' && e.payload.drug === drug)
      .sort((a, b) => Date.parse(b.occurred_at) - Date.parse(a.occurred_at) || b.sequence - a.sequence)[0]?.payload
      .status as string) || 'Not documented'
  );
}

export const newbornExamItems = [
  ['Skin', 'Skin'],
  ['Head', 'Head / fontanelles'],
  ['Eyes', 'Eyes / red reflex'],
  ['Ears', 'Ears'],
  ['Nose', 'Nose'],
  ['Mouth', 'Mouth / palate'],
  ['Neck', 'Neck / clavicles'],
  ['Chest', 'Chest / lungs'],
  ['Heart', 'Heart / murmur'],
  ['Heart', 'Femoral pulses'],
  ['Abdomen', 'Abdomen / cord'],
  ['Genitalia', 'Genitalia'],
  ['Anus', 'Anus patency'],
  ['Spine', 'Spine'],
  ['Hips', 'Hips / Barlow / Ortolani'],
  ['Extremities', 'Extremities / digits'],
  ['Reflexes', 'Moro'],
  ['Reflexes', 'Suck'],
  ['Reflexes', 'Root'],
  ['Reflexes', 'Grasp'],
] as const;
export type ExamDraft = Record<string, { assessment: '' | 'Normal' | 'Abnormal' | 'Not assessed'; note: string }>;
export function examPayload(draft: ExamDraft): Record<string, unknown> {
  return {
    findings: newbornExamItems.flatMap(([section, item]) => {
      const value = draft[item];
      if (!value?.assessment && !value?.note.trim()) return [];
      return [
        {
          section,
          item,
          ...(value.assessment === 'Normal'
            ? { normal: true }
            : value.assessment === 'Abnormal'
              ? { normal: false }
              : {}),
          ...(value.assessment === 'Not assessed' || value.note.trim()
            ? {
                note: [value.assessment === 'Not assessed' ? 'Not assessed' : '', value.note.trim()]
                  .filter(Boolean)
                  .join(': '),
              }
            : {}),
        },
      ];
    }),
  };
}
